<template>
  <div class="p-4 md:p-6 lg:p-8 bg-app-bg-light dark:bg-app-bg-dark min-h-screen">
    <!-- Header -->
    <div class="flex items-center gap-3 mb-6">
      <button
        @click="$router.push('/inicio')"
        class="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 hover:text-custom-primary dark:hover:text-custom-primary transition-colors border-0 bg-transparent"
      >
        <i class="bx bx-arrow-back text-lg"></i>
        Regresar
      </button>
      <div class="h-5 w-px bg-slate-300 dark:bg-slate-600"></div>
      <div>
        <h1 class="text-xl md:text-2xl font-bold text-slate-800 dark:text-slate-100">No Facturado</h1>
        <p class="text-xs text-slate-500 dark:text-slate-400">Cobros en efectivo no facturado (Hospedaje + Restaurante + Servicios Extra + Tienda)</p>
      </div>
    </div>

    <!-- Filtros -->
    <div class="bg-white dark:bg-slate-800 rounded-xl p-4 md:p-6 shadow-sm mb-6">
      <div class="flex flex-wrap gap-3 items-end">
        <div>
          <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Desde</label>
          <input type="date" v-model="filtro.fecha_inicio" class="form-control form-control-sm" />
        </div>
        <div>
          <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Hasta</label>
          <input type="date" v-model="filtro.fecha_fin" class="form-control form-control-sm" />
        </div>
        <div>
          <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Hora inicio</label>
          <input type="time" v-model="filtro.hora_inicio" class="form-control form-control-sm" />
        </div>
        <div>
          <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Hora fin</label>
          <input type="time" v-model="filtro.hora_fin" class="form-control form-control-sm" />
        </div>
        <button @click="cargar" class="btn btn-outline-secondary btn-sm">
          <i class="bx bx-search mr-1"></i> Filtrar
        </button>
      </div>
    </div>

    <div v-if="loading" class="text-center py-10">
      <i class="bx bx-loader-alt bx-spin text-3xl text-custom-primary"></i>
    </div>

    <div v-else>
      <!-- Tarjetas resumen -->
      <div class="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div class="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm">
          <p class="text-xs text-slate-500 dark:text-slate-400">Hospedaje</p>
          <p class="text-lg font-bold text-slate-800 dark:text-slate-100">Q{{ fmt(resumen.hospedaje.total) }}</p>
          <p class="text-xs text-slate-400">{{ resumen.hospedaje.cantidad }} pago(s)</p>
        </div>
        <div class="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm">
          <p class="text-xs text-slate-500 dark:text-slate-400">Restaurante</p>
          <p class="text-lg font-bold text-slate-800 dark:text-slate-100">Q{{ fmt(resumen.restaurante.total) }}</p>
          <p class="text-xs text-slate-400">{{ resumen.restaurante.cantidad }} pago(s)</p>
        </div>
        <div class="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm">
          <p class="text-xs text-slate-500 dark:text-slate-400">Servicios Extra</p>
          <p class="text-lg font-bold text-slate-800 dark:text-slate-100">Q{{ fmt(resumen.servicios_extra.total) }}</p>
          <p class="text-xs text-slate-400">{{ resumen.servicios_extra.cantidad }} pago(s)</p>
        </div>
        <div class="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm">
          <p class="text-xs text-slate-500 dark:text-slate-400">Tienda</p>
          <p class="text-lg font-bold text-slate-800 dark:text-slate-100">Q{{ fmt(resumen.tienda.total) }}</p>
          <p class="text-xs text-slate-400">{{ resumen.tienda.cantidad }} pago(s)</p>
        </div>
      </div>

      <!-- Total general + enlace a caja chica admin -->
      <div class="bg-custom-primary/10 dark:bg-custom-primary/20 rounded-xl p-5 md:p-6 shadow-sm mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <p class="text-xs font-semibold text-custom-primary uppercase tracking-wider mb-1">Total No Facturado</p>
          <p class="text-3xl font-bold text-custom-primary">Q{{ fmt(resumen.total_general) }}</p>
        </div>
        <div class="text-right">
          <p class="text-xs text-slate-500 dark:text-slate-400 mb-1">
            Saldo actual de la Caja Chica de Administración
          </p>
          <p class="text-lg font-bold text-slate-800 dark:text-slate-100 mb-2">Q{{ fmt(resumen.saldo_caja_chica_admin) }}</p>
          <router-link to="/contabilidad/caja-chica-admin" class="btn btn-primary btn-sm">
            <i class="bx bx-wallet mr-1"></i> Ir a Caja Chica Administración
          </router-link>
        </div>
      </div>

      <!-- Detalle -->
      <div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm overflow-hidden">
        <div class="overflow-x-auto">
          <table class="w-full text-xs md:text-sm">
            <thead>
              <tr class="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
                <th class="px-3 py-3 text-left font-semibold text-slate-700 dark:text-slate-300">Módulo</th>
                <th class="px-3 py-3 text-left font-semibold text-slate-700 dark:text-slate-300">Fecha</th>
                <th class="px-3 py-3 text-left font-semibold text-slate-700 dark:text-slate-300">Referencia</th>
                <th class="px-3 py-3 text-right font-semibold text-slate-700 dark:text-slate-300">Monto</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!detalle.length">
                <td colspan="4" class="text-center py-6 text-slate-400">No hay pagos no facturados en el período seleccionado.</td>
              </tr>
              <tr v-for="(p, i) in detalle" :key="i" class="border-b border-slate-100 dark:border-slate-700">
                <td class="px-3 py-2">
                  <span class="badge" :class="badgeClass(p.modulo)">{{ p.modulo }}</span>
                </td>
                <td class="px-3 py-2 whitespace-nowrap">{{ formatFecha(p.fecha_hora) }}</td>
                <td class="px-3 py-2">{{ p.referencia }}</td>
                <td class="px-3 py-2 text-right font-mono">{{ fmt(p.monto) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { API_URL } from '../../config/config.js';

export default {
  name: 'NoFacturado',
  data() {
    const hoy = new Date().toISOString().substring(0, 10);
    return {
      loading: false,
      filtro: { fecha_inicio: hoy, fecha_fin: hoy, hora_inicio: '08:00', hora_fin: '08:00' },
      resumen: {
        hospedaje: { total: 0, cantidad: 0, pagos: [] },
        restaurante: { total: 0, cantidad: 0, pagos: [] },
        servicios_extra: { total: 0, cantidad: 0, pagos: [] },
        tienda: { total: 0, cantidad: 0, pagos: [] },
        total_general: 0,
        saldo_caja_chica_admin: 0,
      },
    };
  },
  computed: {
    detalle() {
      const conModulo = (pagos, modulo) => (pagos || []).map((p) => ({ ...p, modulo }));
      return [
        ...conModulo(this.resumen.hospedaje.pagos, 'Hospedaje'),
        ...conModulo(this.resumen.restaurante.pagos, 'Restaurante'),
        ...conModulo(this.resumen.servicios_extra.pagos, 'Servicios Extra'),
        ...conModulo(this.resumen.tienda.pagos, 'Tienda'),
      ].sort((a, b) => new Date(b.fecha_hora) - new Date(a.fecha_hora));
    },
  },
  methods: {
    fmt(num) {
      return new Intl.NumberFormat('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num ?? 0);
    },
    formatFecha(f) {
      if (!f) return '—';
      return new Date(f.replace(' ', 'T')).toLocaleString('es-GT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    },
    badgeClass(modulo) {
      const classes = {
        Hospedaje: 'bg-primary',
        Restaurante: 'bg-warning text-dark',
        'Servicios Extra': 'bg-info text-dark',
        Tienda: 'bg-secondary',
      };
      return classes[modulo] || 'bg-light text-dark';
    },
    async cargar() {
      this.loading = true;
      try {
        const { data } = await axios.get(`${API_URL}/no-facturado`, { params: this.filtro });
        this.resumen = data;
      } catch (e) {
        console.error(e);
        alert(e.response?.data?.message || 'Error al cargar el reporte de no facturado.');
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    this.cargar();
  },
};
</script>
